import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2, Users, Mail, CreditCard, Plus, Trash2, Shield, ShieldCheck,
  User as UserIcon, Copy, Check, AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import orgService from '../services/organizationService';

const ROLE_STYLE = {
  owner: { icon: ShieldCheck, badge: 'bg-purple-100 text-purple-800 border-purple-300' },
  admin: { icon: Shield, badge: 'bg-blue-100 text-blue-800 border-blue-300' },
  member: { icon: UserIcon, badge: 'bg-gray-100 text-gray-700 border-gray-300' },
};

const ROLE_HINT = {
  owner: 'Full control, including billing and deleting the organization.',
  admin: 'Manage members, images and clusters. Cannot change billing.',
  member: 'Use the organization\'s resources. Cannot manage members or billing.',
};

const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const OrganizationsPage = () => {
  const { isAdmin } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [newOrgName, setNewOrgName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [copiedToken, setCopiedToken] = useState(null);

  const myRole = selected?.member_role;
  const canManageMembers = myRole === 'owner' || myRole === 'admin';
  const canManageBilling = myRole === 'owner';

  const loadOrgs = useCallback(async () => {
    try {
      const data = await orgService.listOrganizations();
      setOrgs(data.organizations || []);
      setSelected((cur) => {
        if (cur) return (data.organizations || []).find((o) => o.id === cur.id) || null;
        const activeId = orgService.getActiveOrganizationId();
        return (data.organizations || []).find((o) => o.id === activeId)
          || (data.organizations || [])[0] || null;
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);

  const loadDetail = useCallback(async (org) => {
    if (!org) return;
    setError(null);
    try {
      // Members and billing are readable by any member; invitations are not,
      // so a plain member must not see a failure for something they simply
      // cannot access.
      const [m, b] = await Promise.all([
        orgService.listMembers(org.id),
        orgService.getBilling(org.id),
      ]);
      setMembers(m.members || []);
      setBilling(b.billing || null);
      if (org.member_role === 'owner' || org.member_role === 'admin') {
        const inv = await orgService.listInvitations(org.id);
        setInvitations(inv.invitations || []);
      } else {
        setInvitations([]);
      }
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { loadDetail(selected); }, [selected, loadDetail]);

  const act = async (fn, successMsg) => {
    setError(null); setNotice(null);
    try {
      await fn();
      if (successMsg) setNotice(successMsg);
      await loadOrgs();
      await loadDetail(selected);
    } catch (e) {
      setError(e.message);
    }
  };

  if (loading) {
    return <div className="p-8 text-sm text-gray-500">Loading organizations…</div>;
  }

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 size={22} /> Organizations
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Share compute, images and a single credit pool across a team.
            Resources created outside an organization stay personal.
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}
      {notice && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex justify-between">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="text-green-600 hover:text-green-800">&times;</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ── Org list + create ── */}
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow border p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Your organizations</h3>
            {orgs.length === 0 && (
              <p className="text-xs text-gray-500">
                You are not in any organization yet. Create one below.
              </p>
            )}
            <div className="space-y-1">
              {orgs.map((o) => {
                const Style = ROLE_STYLE[o.member_role] || ROLE_STYLE.member;
                const Icon = Style.icon;
                const active = selected?.id === o.id;
                return (
                  <button
                    key={o.id}
                    onClick={() => { setSelected(o); orgService.setActiveOrganizationId(o.id); }}
                    className={`w-full text-left px-3 py-2 rounded-lg border text-sm flex items-center justify-between ${
                      active ? 'bg-purple-50 border-purple-300' : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <span className="truncate">
                      <span className="font-medium text-gray-900">{o.name}</span>
                      <span className="block text-[11px] text-gray-400">/{o.slug}</span>
                    </span>
                    <span className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${Style.badge} flex items-center gap-1`}>
                      <Icon size={10} /> {o.member_role}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow border p-4 space-y-2">
            <h3 className="text-sm font-medium text-gray-900">Create an organization</h3>
            <input
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              placeholder="Organization name"
              className="w-full px-3 py-1.5 border rounded-md text-sm"
            />
            <button
              disabled={!newOrgName.trim()}
              onClick={() => act(
                () => orgService.createOrganization(newOrgName.trim()),
                `Created "${newOrgName.trim()}" — you are its owner.`,
              ).then(() => setNewOrgName(''))}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-40"
            >
              <Plus size={14} /> Create
            </button>
            <p className="text-[11px] text-gray-400">
              You become the owner. Credit is separate from your personal balance.
            </p>
          </div>
        </div>

        {/* ── Detail ── */}
        <div className="lg:col-span-2 space-y-4">
          {!selected && (
            <div className="bg-white rounded-lg shadow border p-6 text-sm text-gray-500">
              Select an organization to manage it.
            </div>
          )}

          {selected && (
            <>
              {/* Billing */}
              <div className="bg-white rounded-lg shadow border p-4">
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-1.5 mb-3">
                  <CreditCard size={15} /> Shared credit
                </h3>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-[11px] text-gray-500 uppercase">Balance</p>
                    <p className="text-lg font-semibold text-gray-900">{money(billing?.balance_cents)}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-[11px] text-gray-500 uppercase">Spent this period</p>
                    <p className="text-lg font-semibold text-gray-900">{money(billing?.current_period_spent_cents)}</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-[11px] text-gray-500 uppercase">Limit</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {billing?.spending_limit_cents != null ? money(billing.spending_limit_cents) : '—'}
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-gray-400 mt-2">
                  Clusters launched into this organization bill this pool, not the
                  launcher's personal balance. A cluster is auto-stopped when the
                  pool cannot fund another hour, or the period limit is reached.
                </p>
                {canManageBilling && (
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => {
                        const v = window.prompt('Spending limit in dollars for the period (blank to clear):');
                        if (v === null) return;
                        const cents = v.trim() === '' ? null : Math.round(parseFloat(v) * 100);
                        act(() => orgService.setSpendingLimit(selected.id, cents, cents == null ? null : 'monthly'),
                          'Spending limit updated.');
                      }}
                      className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
                    >
                      Set limit
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => {
                          const v = window.prompt('Grant how many dollars of credit?');
                          if (!v) return;
                          act(() => orgService.grantCredit(selected.id, Math.round(parseFloat(v) * 100), 'Admin grant'),
                            'Credit granted.');
                        }}
                        className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
                      >
                        Grant credit (admin)
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Members */}
              <div className="bg-white rounded-lg shadow border p-4">
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-1.5 mb-3">
                  <Users size={15} /> Members ({members.length})
                </h3>
                <div className="divide-y divide-gray-100">
                  {members.map((m) => {
                    const Style = ROLE_STYLE[m.role] || ROLE_STYLE.member;
                    const Icon = Style.icon;
                    return (
                      <div key={m.user_id} className="py-2 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm text-gray-900 truncate">{m.email}</p>
                          <p className="text-[11px] text-gray-400">
                            account role: {m.account_role}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${Style.badge} flex items-center gap-1`}>
                            <Icon size={10} /> {m.role}
                          </span>
                          {canManageMembers && (
                            <select
                              value={m.role}
                              onChange={(e) => act(
                                () => orgService.setMemberRole(selected.id, m.user_id, e.target.value),
                                `${m.email} is now ${e.target.value}.`,
                              )}
                              className="text-xs border rounded px-1 py-0.5"
                              title={ROLE_HINT[m.role]}
                            >
                              <option value="member">member</option>
                              <option value="admin">admin</option>
                              {/* Only an owner can grant ownership; the server
                                  enforces this too. */}
                              {myRole === 'owner' && <option value="owner">owner</option>}
                            </select>
                          )}
                          {canManageMembers && (
                            <button
                              onClick={() => {
                                if (!window.confirm(`Remove ${m.email} from ${selected.name}?`)) return;
                                act(() => orgService.removeMember(selected.id, m.user_id), 'Member removed.');
                              }}
                              className="p-1 text-gray-400 hover:text-red-600 rounded"
                              title="Remove from organization"
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Invitations */}
              {canManageMembers && (
                <div className="bg-white rounded-lg shadow border p-4">
                  <h3 className="text-sm font-medium text-gray-900 flex items-center gap-1.5 mb-3">
                    <Mail size={15} /> Invitations
                  </h3>
                  <div className="flex gap-2 mb-3">
                    <input
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="person@example.com"
                      className="flex-1 px-3 py-1.5 border rounded-md text-sm"
                    />
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      className="border rounded-md text-sm px-2"
                    >
                      <option value="member">member</option>
                      <option value="admin">admin</option>
                      {myRole === 'owner' && <option value="owner">owner</option>}
                    </select>
                    <button
                      disabled={!inviteEmail.trim()}
                      onClick={() => act(
                        () => orgService.inviteMember(selected.id, inviteEmail.trim(), inviteRole),
                        'Invitation created — copy the link below and send it.',
                      ).then(() => setInviteEmail(''))}
                      className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-40"
                    >
                      Invite
                    </button>
                  </div>

                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 mb-2">
                    Invitations are not emailed yet — copy the link and send it
                    yourself. It only works for the exact address invited, and
                    expires after 7 days.
                  </p>

                  <div className="divide-y divide-gray-100">
                    {invitations.length === 0 && (
                      <p className="text-xs text-gray-400 py-2">No invitations.</p>
                    )}
                    {invitations.map((inv) => {
                      const link = `${window.location.origin}/?invite=${inv.token || ''}`;
                      return (
                        <div key={inv.id} className="py-2 flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm text-gray-900 truncate">{inv.email}</p>
                            <p className="text-[11px] text-gray-400">
                              {inv.role} · {inv.accepted_at ? `accepted ${inv.accepted_at}` : `expires ${inv.expires_at}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {!inv.accepted_at && inv.token && (
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(link);
                                  setCopiedToken(inv.id);
                                  setTimeout(() => setCopiedToken(null), 1500);
                                }}
                                className="p-1 text-gray-400 hover:text-purple-600 rounded"
                                title="Copy invite link"
                              >
                                {copiedToken === inv.id ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
                              </button>
                            )}
                            {!inv.accepted_at && (
                              <button
                                onClick={() => act(
                                  () => orgService.revokeInvitation(selected.id, inv.id),
                                  'Invitation revoked.',
                                )}
                                className="p-1 text-gray-400 hover:text-red-600 rounded"
                                title="Revoke"
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationsPage;
